"use client";

import { useTheme } from "next-themes";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { Linkedin } from "geist-icons";
import { GithubIcon } from "../home/about/GithubIcon";
import { usePathname } from "next/navigation";
import { Skeleton } from "./skeleton";

export default function Footer() {
  const [mounted, setMounted] = useState(false);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
  }, []);

  return (
    <footer
      className="bg-[#FAFAFA] dark:bg-black min-w-[320px] pt-20 pb-10 md:py-20 w-full px-4 sm:px-6 md:px-8 lg:px-10"
    >
      <div className="max-w-[1100px] w-full mx-auto gap-10 md:gap-0 flex flex-col-reverse md:flex-row">
        {/* Main Grid */}
        <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0">
          {/* Products */}
          <div>
            <h4 className="text-sm font-normal mb-4">Services</h4>
            <ul className="space-y-2 text-sm text-[#666666] dark:text-[#A0A0A0]">
              <li>
                <Link
                  href="/service#websites"
                  className="hover:text-black hover:dark:text-white"
                >
                  Websites
                </Link>
              </li>
              <li>
                <Link
                  href="/service#webapps"
                  className="hover:text-black hover:dark:text-white"
                >
                  Web Apps
                </Link>
              </li>
              <li>
                <Link
                  href="/service#ai"
                  className="hover:text-black hover:dark:text-white"
                >
                  AI Integrations
                </Link>
              </li>
              <li>
                <Link
                  href="/service#webvitals"
                  className="hover:text-black hover:dark:text-white"
                >
                  Web Vitals
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-sm font-normal mb-4">Resources</h4>
            <ul className="space-y-2 text-sm text-[#666666] dark:text-[#A0A0A0]">
              <li>
                <Link
                  href="/tools#nextjs"
                  className="hover:text-black hover:dark:text-white"
                >
                  Nextjs
                </Link>
              </li>
              <li>
                <Link
                  href="/tools"
                  className="hover:text-black hover:dark:text-white"
                >
                  AI
                </Link>
              </li>
              <li>
                <Link
                  href="/tools"
                  className="hover:text-black hover:dark:text-white"
                >
                  Vercel
                </Link>
              </li>
              <li>
                <Link
                  href="/portfolio"
                  className="hover:text-black hover:dark:text-white"
                >
                  Portfolio
                </Link>
              </li>
            </ul>
          </div>

          {/* Me */}
          <div>
            <h4 className="text-sm font-normal mb-4">Me</h4>
            <ul className="space-y-2 text-sm text-[#666666] dark:text-[#A0A0A0]">
              <li>
                <Link
                  href="/about"
                  className="hover:text-black hover:dark:text-white"
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  href="/contact"
                  className="hover:text-black hover:dark:text-white"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  href="/privacy"
                  className="hover:text-black hover:dark:text-white"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  href="/terms"
                  className="hover:text-black hover:dark:text-white"
                >
                  Terms & Conditions
                </Link>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-sm font-normal mb-4">Social</h4>
            <ul className="space-y-2 text-sm text-[#666666] dark:text-[#A0A0A0]">
              <li>
                <Link
                  href="https://github.com/alx-pros"
                  target="_blank"
                  className="group inline-flex gap-1"
                >
                  <GithubIcon className="group-hover:text-black dark:group-hover:text-white scale-60 -mt-1 -ml-1" />
                  <span className="group-hover:text-black dark:group-hover:text-white">
                    GitHub
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  href="https://www.linkedin.com/in/alex-prostamo-245809235"
                  target="_blank"
                  className="inline-flex gap-1 hover:text-black hover:dark:text-white"
                >
                  <Linkedin className="scale-60" />
                  LinkedIn
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div>
          {mounted ? (
            <Link href="/">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 50 600 600"
                className="relative w-6 h-6 scale-130"
              >
                <g id="Layer 1">
                  <g>
                    <path
                      className="fill-black dark:fill-white transition-all duration-300"
                      d="m164.8 502.7l4.4-8.3 0.9-1.9 1.8-3.5 2-3.9 85.4-171.9 7.4 17.1h52.3l-22.6-51.8-22.4-40.8-1.5-1-0.8-0.5q-0.4-0.2-0.7-0.4c-6.3-3-13.5-2.5-20.1 0.9q-1.4 0.7-2.7 1.6c-4.9 3.2-9.2 8.1-12.3 14.1l-97.5 196.2q-2.9-5.4-5.6-11.1c-49.8-106.1-4.2-232.6 101.9-282.5 46.2-21.7 96.3-25.3 142.3-13.7-53.8-18.1-114.3-16.4-169.7 9.7-113.9 53.5-162.8 189.1-109.3 302.9 13.4 28.6 32 53 54.2 72.8q0.1 0.1 0.1 0.2l12.5-24.2z"
                    />
                    <path
                      className="fill-black dark:fill-white transition-all duration-300"
                      d="m324 341.7h-52.3-8.4-0.2-0.3-2l-76.9 148.6-5.5 10.6q0 0-0.1-0.1l-17.3 33.5q0.1 0 0.1 0c65.7 52.9 158.4 67.1 239.9 28.8 55.3-26 95.4-71.5 115.7-124.4-20.4 42.7-55.1 79-101.4 100.7-67.4 31.7-143.1 24.8-202.1-11.6l74-143.2h3.2 52.3 38.7l26.6-0.5c33.9 0 61.6-29.1 61.6-64.6v-18.2c0-35.5-27.7-64.6-61.6-64.6h-117l18.1 41.8h92.8c16.7 0 30.4 14.4 30.4 31.9 0 8.7-3.4 16.7-8.9 22.5-5.5 5.8-13.1 9.3-21.5 9.3h-20.5"
                    />
                    <path
                      className="fill-black dark:fill-white transition-all duration-300"
                      d="m329.3 473.6l43.9-19.2-25.5-58.4h-52.3z"
                    />
                    <path
                      className="fill-black dark:fill-white transition-all duration-300"
                      d="M324 341.7h54.1.2l3.1 0.3"
                    />
                  </g>
                </g>
              </svg>
            </Link>
          ) : (
            // Placeholder to prevent layout shift during hydration
            <Skeleton className="w-6 h-6 rounded-full" />
          )}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="max-w-[1100px] mx-auto pt-14 flex flex-col md:flex-row justify-between items-center gap-0 text-xs text-[#666666] dark:text-[#A0A0A0]">
        <p>Copyright © {currentYear} AlxPro. All rights reserved.</p>
        <p className="text-center md:text-right md:w-3/5">
          Design inspired by{" "}
          <a
            href="https://vercel.com/home"
            target="_blank"
            className="underline hover:text-black dark:hover:text-white"
          >
            Vercel
          </a>{" "}
          and{" "}
          <a
            href="https://vercel.com/geist/introduction"
            target="_blank"
            className="underline hover:text-black dark:hover:text-white"
          >
            Geist Design System
          </a>{" "}
          including layout ideas, <br className="hidden md:block lg:hidden" />
          components, and iconography.{" "}
          <br className="hidden md:hidden lg:block" /> All elements have been
          adapted <br className="hidden md:block lg:hidden" /> and restructured
          to match my own{" "}
          <span className="whitespace-nowrap sm:whitespace-normal">
            creative vision.
          </span>
        </p>
      </div>
    </footer>
  );
}
